import type { JSX } from 'react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { SYSTEM_PROMPT_MAX_CHARS } from '@logistics/contracts';
import { cn } from '@/lib/utils';
import { api } from '../client';
import { button, card, formError, segment } from '../shell/look';
import { useToast } from '../shell/toast';
import { agentFormSchema, agentFormValues, languageLabel, toneLabel } from './agent-schema';
import type { AgentForm } from './agent-schema';
import { Dropdown } from './dropdown';
import { AGENT_KEY } from './use-agent';
import type { AgentSettingsResponse } from './use-agent';

/** The settings card. Everyone may read it; only an admin may save. */
export function AgentEditor({
  loaded,
  admin,
}: {
  loaded: AgentSettingsResponse;
  admin: boolean;
}): JSX.Element {
  const form = useForm<AgentForm>({
    resolver: zodResolver(agentFormSchema),
    defaultValues: agentFormValues(loaded.settings, loaded.models),
  });
  const [problems, setProblems] = useState<readonly string[]>([]);
  const client = useQueryClient();
  const toast = useToast();

  const save = form.handleSubmit(async (values) => {
    setProblems([]);
    const r = await api.agent.save({ body: values });
    if (r.status !== 200) {
      const body = r.body as { problems?: string[]; message?: string } | null;
      return setProblems(body?.problems ?? [body?.message ?? `The API answered ${r.status}.`]);
    }
    client.setQueryData<AgentSettingsResponse>(AGENT_KEY, { ...loaded, settings: r.body.settings });
    toast('Agent settings saved');
  });

  const prompt = form.watch('systemPrompt');
  const invalid = Object.values(form.formState.errors).find((e) => e?.message !== undefined);
  const errors = invalid?.message !== undefined ? [invalid.message] : problems;
  const status = !admin
    ? 'Read-only · only an admin can change these'
    : form.formState.isDirty
      ? 'Unsaved changes'
      : 'Saved · applies to Telegram and Web';

  return (
    <form
      noValidate
      onSubmit={(e) => void save(e)}
      className={cn(card, 'flex flex-col gap-[22px] p-6')}
    >
      <Choices form={form} loaded={loaded} admin={admin} />
      <label className="text-mute flex flex-col gap-1.5 text-[13px]">
        <span className="flex justify-between">
          System prompt
          <span
            className={cn(
              'font-mono text-[11.5px]',
              prompt.length > SYSTEM_PROMPT_MAX_CHARS && 'text-bad',
            )}
          >
            {prompt.length} / {SYSTEM_PROMPT_MAX_CHARS}
          </span>
        </span>
        <textarea
          {...form.register('systemPrompt')}
          rows={9}
          readOnly={!admin}
          className="bg-surf2 border-line text-ink resize-y rounded-[9px] border px-3.5 py-3 font-mono text-[13.5px] leading-[1.55]"
        />
      </label>
      {errors.length > 0 && (
        <div role="alert" className={formError}>
          {errors.map((problem) => (
            <div key={problem}>{problem}</div>
          ))}
        </div>
      )}
      <div className="border-line flex items-center justify-between border-t pt-[18px]">
        <span className="text-mute font-mono text-[12.5px]">{status}</span>
        {admin && (
          <button
            type="submit"
            disabled={form.formState.isSubmitting}
            className={cn(button.primary, 'px-[18px]')}
          >
            {form.formState.isSubmitting ? 'Saving…' : 'Save changes'}
          </button>
        )}
      </div>
    </form>
  );
}

/** Model and language dropdowns, and the tone segment. */
function Choices({
  form,
  loaded,
  admin,
}: {
  form: UseFormReturn<AgentForm>;
  loaded: AgentSettingsResponse;
  admin: boolean;
}): JSX.Element {
  const dirty = { shouldDirty: true };
  const tone = form.watch('tone');
  return (
    <>
      <div className="grid grid-cols-1 gap-4 tab:grid-cols-2">
        <Controller
          control={form.control}
          name="model"
          render={({ field }) => (
            <Dropdown
              label="Model"
              mono
              disabled={!admin}
              value={field.value}
              options={loaded.models.map((m) => ({ value: m, label: m }))}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          control={form.control}
          name="language"
          render={({ field }) => (
            <Dropdown
              label="Reply language"
              disabled={!admin}
              value={field.value}
              options={loaded.languages.map((l) => ({ value: l, label: languageLabel(l) }))}
              onChange={field.onChange}
            />
          )}
        />
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-mute text-[13px]">Tone</span>
        <div className={cn(segment.group, 'bg-surf2 w-fit')} role="radiogroup" aria-label="Tone">
          {loaded.tones.map((t) => (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={tone === t}
              disabled={!admin}
              onClick={() => form.setValue('tone', t, dirty)}
              className={cn(tone === t ? segment.on : segment.off, 'px-3.5 py-[7px]')}
            >
              {toneLabel(t)}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
